import React from 'react'
import { Typography } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import palette from '../../../../themes/palette'
import type { Mailer } from '../../../../api/EmailTestBenchApi'
import { ABOUT_DORMANT, ABOUT_HEADING } from './copy'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // "About this email" -- prose, so it is drawn as prose. ContextPanel below
    // it is a bordered box because it holds a CONTROL; boxing this too would
    // make the pane a stack of identical containers and say that a sentence you
    // read and a picker you operate are the same kind of thing.
    about: { marginBottom: theme.spacing(2) },
    // The same small-caps heading idiom as ContextPanel and the catalogue's
    // group headers: a different KIND of text from the body beneath it, which
    // is what makes it read as a heading at this size.
    aboutHeading: {
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontSize: 11,
      color: palette.lightergrey,
      marginBottom: theme.spacing(0.5)
    },
    // Amber, matching the `dormant` chip in the list -- the same fact, so the
    // same colour. The rule carries it when the row is scanned rather than
    // read. Amber rather than red for the reason the chip is: nothing is
    // broken, and this email is still worth looking at.
    aboutDormant: {
      color: palette.orange900,
      borderLeft: `2px solid ${palette.lightOrange}`,
      paddingLeft: theme.spacing(1),
      marginTop: theme.spacing(1)
    }
  })
)

interface Props {
  mailer: Mailer
}

/**
 * "About this email" -- the detail pane's description of the selected mailer.
 *
 * It sits ABOVE the context picker on purpose: you learn what the thing IS
 * before you configure it and fire it. The caller renders it unconditionally
 * and this component decides whether there is anything to say.
 *
 * 🔴 Rendered only when there is something to put in it. Most mailers have no
 * explainer and are not dormant, so most of the time this is nothing at all --
 * no empty box, and no heading floating over blank space. Returning `null`
 * rather than an empty div is load-bearing: a test asserts `#ic-bench-about` is
 * absent, not merely empty.
 *
 * 🔴 The locked reason is deliberately NOT here, though it is the third
 * sentence a locked row could show. It answers "why can't I fire this?", which
 * is a statement about the buttons, not a description of the email -- so it
 * stays where it already lives, in ContextPanel under its "Not available"
 * heading, directly above the actions it is about. That path was dead code
 * until locked rows became selectable; it is now reachable, and rendering the
 * reason here as well would print it twice on the same pane.
 */
const AboutEmail: React.FC<Props> = ({ mailer }) => {
  const classes = useStyles()

  const explainer = mailer.explainer
  const dormancy = mailer.dormant ? mailer.dormant_reason : null

  if (!explainer && !dormancy) return null

  return (
    <div className={classes.about} id='ic-bench-about'>
      <div className={classes.aboutHeading}>{ABOUT_HEADING}</div>
      {explainer && <Typography variant='body2'>{explainer}</Typography>}
      {dormancy && (
        <Typography variant='body2' className={classes.aboutDormant}>
          <strong>{ABOUT_DORMANT}</strong> {dormancy}
        </Typography>
      )}
    </div>
  )
}

export default AboutEmail
